import React from 'react'
import {
  ImageIcon,
  MagicWandIcon,
  OpacityIcon,
  PersonIcon,
  ExclamationTriangleIcon,
} from '@radix-ui/react-icons'
import { useParams } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { Checkbox } from '@/components/ui/checkbox'
import {
  DataTableRowActions,
  DataTableRowCover,
  DataTableRowOpt,
  DataTableRowText,
  ComponentCount,
  CopyId,
} from './data-table-row-actions'
import EditLayer from './layer-editor'
import {
  jobMatchesLayer,
  useCptUploadStore,
} from 'Editor/BottomPanel/BatchUpload/uploadQueue'

const CenteredHeader = ({ children }) => (
  <div className="flex w-full items-center justify-center">{children}</div>
)

const LayerFailedUploadMark = ({ lid }) => {
  const { tid } = useParams()
  const failedCount = useCptUploadStore(
    (state) =>
      state.jobs.filter(
        (job) =>
          job.tid === tid &&
          jobMatchesLayer(job, lid) &&
          job.status === 'error',
      ).length,
  )
  if (!failedCount) return null
  return (
    <span
      className="inline-flex shrink-0 text-destructive"
      title={`${failedCount} 张图上传失败`}
    >
      <ExclamationTriangleIcon className="h-4 w-4" />
    </span>
  )
}

export const getColumns = ({
  selectedIds,
  onToggleSelect,
  allSelected,
  someSelected,
  onToggleAll,
}) => [
  {
    id: 'select',
    header: () => (
      <div className="flex justify-center">
        <Checkbox
          checked={allSelected ? true : someSelected ? 'indeterminate' : false}
          onCheckedChange={(checked) => onToggleAll(checked === true)}
          aria-label="Select all layers"
          onClick={(event) => event.stopPropagation()}
        />
      </div>
    ),
    cell: ({ row }) =>
      row.original.id === 'draft' ? null : (
        <div className="flex justify-center">
          <Checkbox
            checked={selectedIds.has(row.original.id)}
            onCheckedChange={() => onToggleSelect(row.original.id)}
            aria-label={`Select ${row.original.name || row.original.id}`}
            onClick={(event) => event.stopPropagation()}
          />
        </div>
      ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    id: 'drag-handle',
    header: '',
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: 'cover',
    header: '',
    cell: ({ row }) => <DataTableRowCover row={row} />,
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: 'name',
    header: <div></div>,
    cell: ({ row }) => (
      <>
        <div className="flex min-w-0 items-center gap-1">
          <DataTableRowText row={row} entityKey="name" type="text" />
          <LayerFailedUploadMark lid={row.original.id} />
        </div>
        <ComponentCount row={row} />
        <CopyId row={row} />
      </>
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: 'groupName',
    header: () => <CenteredHeader>Group</CenteredHeader>,
    cell: ({ row }) => {
      const { groupName, gid } = row.original
      if (!gid) return <div className="text-center text-sm text-muted-foreground">—</div>
      return (
        <div
          className="max-w-[120px] truncate text-center text-sm"
          title={groupName || gid}
        >
          {groupName || gid}
        </div>
      )
    },
    enableSorting: false,
  },
  {
    accessorKey: 'opt',
    header: <CenteredHeader>Required</CenteredHeader>,
    cell: ({ row }) => <DataTableRowOpt row={row} />,
    enableSorting: false,
    enableHiding: false,
  },
  {
    id: 'mark',
    header: <CenteredHeader>Mark</CenteredHeader>,
    cell: ({ row }) => {
      const layer = row.original
      return (
        row.original.id !== 'draft' && (
          <div className="flex justify-center items-center">
            {layer.watermark === 1 ? (
              <OpacityIcon className={cn('w-5 h-5 text-primary')} />
            ) : (
              <div></div>
            )}
            {layer.bodymark === 1 ? (
              <PersonIcon className={cn('w-5 h-5 text-primary')} />
            ) : (
              <div></div>
            )}
            {layer.background === 1 ? (
              <ImageIcon className={cn('w-5 h-5 text-primary')} />
            ) : (
              <div></div>
            )}
            {layer.toning ? (
              <MagicWandIcon className={cn('w-5 h-5 text-primary')} />
            ) : (
              <div></div>
            )}
          </div>
        )
      )
    },
  },
  {
    accessorKey: 'z',
    header: () => <div className="text-left">Z index</div>,
    cell: ({ row }) => {
      return <DataTableRowText row={row} entityKey="z" type="number" />
    },
  },
  {
    accessorKey: 'playIndex',
    header: () => <CenteredHeader>Play index</CenteredHeader>,
    cell: ({ row }) => (
      <div className="flex items-center justify-center">
        <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-mono text-primary">
          {row.original.playIndex}
        </div>
      </div>
    ),
    enableSorting: false,
  },
  {
    id: 'edit',
    enableHiding: false,
    cell: ({ row }) => <EditLayer layer={row.original} />,
  },
  {
    id: 'actions',
    enableHiding: false,
    cell: ({ row }) => <DataTableRowActions row={row} />,
  },
]
